import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './TeacherSections.css';

const TeacherSectionEarnings = () => {
  const { t } = useTranslation('teacher');
  const [activeTab, setActiveTab] = useState('overview');
  const [earningsData, setEarningsData] = useState({
    totalEarnings: 12800,
    thisMonth: 2450,
    pendingPayouts: 1850,
    withdrawnSoFar: 8500,
    conversionRate: 12.5,
    topRevenueSource: 'Live Sessions'
  });

  const [earningsHistory, setEarningsHistory] = useState([
    {
      id: 1,
      date: '2024-03-15',
      source: 'Live Session',
      amount: 60.00,
      status: 'paid',
      student: 'Sarah Johnson',
      notes: 'Advanced Speaking Practice - 1h Zoom Class',
      type: 'live-session'
    },
    {
      id: 2,
      date: '2024-03-14',
      source: 'Course Sale',
      amount: 49.99,
      status: 'paid',
      student: 'Anonymous',
      notes: 'Advanced English Grammar Course',
      type: 'course-sale'
    },
    {
      id: 3,
      date: '2024-03-13',
      source: 'Vocabulary Quiz',
      amount: 8.00,
      status: 'pending',
      student: 'Mike Chen',
      notes: 'Business English Vocabulary - 5 quiz sessions',
      type: 'vocabulary'
    },
    {
      id: 4,
      date: '2024-03-12',
      source: 'Live Session',
      amount: 45.00,
      status: 'paid',
      student: 'Anna Lopez',
      notes: 'IELTS Writing Practice - 1.5h Session',
      type: 'live-session'
    },
    {
      id: 5,
      date: '2024-03-11',
      source: 'Student Tip',
      amount: 15.00,
      status: 'paid',
      student: 'David Kim',
      notes: 'Thank you for the great lesson!',
      type: 'tip'
    }
  ]);

  const [revenueByType, setRevenueByType] = useState([
    { source: 'Live Sessions', earnings: 130, percentage: 32, color: '#3498db' },
    { source: 'Course Sales', earnings: 220, percentage: 55, color: '#27ae60' },
    { source: 'Vocabulary Games', earnings: 30, percentage: 8, color: '#f39c12' },
    { source: 'Student Tips', earnings: 20, percentage: 5, color: '#e74c3c' }
  ]);

  const [paymentMethods, setPaymentMethods] = useState([
    { id: 1, type: 'PayPal', email: 'ms.smith@email.com', isDefault: true },
    { id: 2, type: 'Bank Transfer', account: '****1234', isDefault: false },
    { id: 3, type: 'Wise', email: 'ms.smith@wise.com', isDefault: false }
  ]);

  const [selectedPeriod, setSelectedPeriod] = useState('this-month');
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(1);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      paid: { class: 'success', text: t('earnings.history.paid') },
      pending: { class: 'warning', text: t('earnings.history.pending') },
      processing: { class: 'info', text: t('earnings.history.processing') },
      failed: { class: 'danger', text: t('earnings.history.failed') }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    return <span className={`status-badge ${config.class}`}>{config.text}</span>;
  };

  const getSourceIcon = (type) => {
    const icons = {
      'live-session': 'video_call',
      'course-sale': 'menu_book',
      'vocabulary': 'library_books',
      'tip': 'favorite'
    };
    return icons[type] || 'attach_money';
  };

  const handleWithdraw = (e) => {
    e.preventDefault();
    if (withdrawAmount && parseFloat(withdrawAmount) > 0) {
      console.log(`Withdrawing $${withdrawAmount}`);
      setShowWithdrawModal(false);
      setWithdrawAmount('');
    }
  };

  const exportEarnings = (format) => {
    console.log(`Exporting earnings as ${format}`);
    // In a real app, this would generate and download the file
  };

  const [analyticsData, setAnalyticsData] = useState({
    topStudents: [
      { name: 'Sarah Johnson', totalSpent: 450, purchases: 8, lastPurchase: '2024-03-15', courses: ['Advanced English Grammar', 'Business Speaking'] },
      { name: 'Mike Chen', totalSpent: 320, purchases: 5, lastPurchase: '2024-03-12', courses: ['IELTS Writing', 'Vocabulary Builder'] },
      { name: 'Anna Lopez', totalSpent: 280, purchases: 4, lastPurchase: '2024-03-10', courses: ['Grammar Review', 'Pronunciation'] },
      { name: 'David Kim', totalSpent: 195, purchases: 3, lastPurchase: '2024-03-08', courses: ['Business English', 'Live Sessions'] },
      { name: 'Emma Wilson', totalSpent: 165, purchases: 2, lastPurchase: '2024-03-05', courses: ['Beginner English', 'Speaking Practice'] }
    ],
    topCourses: [
      { name: 'Advanced English Grammar', revenue: 1200, enrollments: 45, avgRating: 4.8, completionRate: 78 },
      { name: 'Business English Speaking', revenue: 980, enrollments: 32, avgRating: 4.6, completionRate: 82 },
      { name: 'IELTS Writing Practice', revenue: 850, enrollments: 28, avgRating: 4.9, completionRate: 75 },
      { name: 'Vocabulary Builder Pro', revenue: 720, enrollments: 25, avgRating: 4.7, completionRate: 80 },
      { name: 'Pronunciation Mastery', revenue: 650, enrollments: 22, avgRating: 4.5, completionRate: 85 }
    ],
    earningsByTime: {
      byDay: [
        { day: 'Monday', earnings: 180, sessions: 4 },
        { day: 'Tuesday', earnings: 220, sessions: 5 },
        { day: 'Wednesday', earnings: 195, sessions: 4 },
        { day: 'Thursday', earnings: 250, sessions: 6 },
        { day: 'Friday', earnings: 280, sessions: 7 },
        { day: 'Saturday', earnings: 320, sessions: 8 },
        { day: 'Sunday', earnings: 200, sessions: 5 }
      ],
      byHour: [
        { hour: '9 AM', earnings: 45, sessions: 1 },
        { hour: '10 AM', earnings: 90, sessions: 2 },
        { hour: '11 AM', earnings: 135, sessions: 3 },
        { hour: '12 PM', earnings: 60, sessions: 1 },
        { hour: '1 PM', earnings: 75, sessions: 2 },
        { hour: '2 PM', earnings: 120, sessions: 3 },
        { hour: '3 PM', earnings: 180, sessions: 4 },
        { hour: '4 PM', earnings: 150, sessions: 3 },
        { hour: '5 PM', earnings: 200, sessions: 5 },
        { hour: '6 PM', earnings: 240, sessions: 6 },
        { hour: '7 PM', earnings: 180, sessions: 4 },
        { hour: '8 PM', earnings: 120, sessions: 3 }
      ]
    }
  });

  const tabs = [
    { id: 'overview', label: t('earnings.tabs.overview'), icon: 'dashboard' },
    { id: 'revenue', label: t('earnings.tabs.revenue'), icon: 'pie_chart' },
    { id: 'payments', label: t('earnings.tabs.payments'), icon: 'account_balance_wallet' },
    { id: 'history', label: t('earnings.tabs.history'), icon: 'history' },
    { id: 'analytics', label: t('earnings.tabs.analytics'), icon: 'analytics' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="teacher-section">
            <div className="teacher-section-header">
              <div className="teacher-section-title">
                <i className="material-icons">receipt_long</i>
                {t('earnings.overview.title')}
              </div>
              <div className="teacher-header-actions">
                <button className="teacher-btn secondary" onClick={() => exportEarnings('csv')}>
                  <i className="material-icons">download</i>
                  {t('earnings.overview.exportCSV')}
                </button>
                <button className="teacher-btn secondary" onClick={() => exportEarnings('pdf')}>
                  <i className="material-icons">picture_as_pdf</i>
                  {t('earnings.overview.downloadPDF')}
                </button>
              </div>
            </div>

            <div className="teacher-overview-grid">
              <div className="teacher-overview-card accent">
                <div className="teacher-overview-header">
                  <span className="teacher-overview-title">{t('earnings.overview.totalEarnings')}</span>
                  <div className="teacher-overview-icon">
                    <i className="material-icons">account_balance_wallet</i>
                  </div>
                </div>
                <div className="teacher-overview-value">{formatCurrency(earningsData.totalEarnings)}</div>
                <div className="teacher-overview-change">
                  <i className="material-icons">trending_up</i>
                  {t('earnings.overview.allTimeEarnings')}
                </div>
              </div>

              <div className="teacher-overview-card success">
                <div className="teacher-overview-header">
                  <span className="teacher-overview-title">{t('earnings.overview.thisMonth')}</span>
                  <div className="teacher-overview-icon">
                    <i className="material-icons">calendar_today</i>
                  </div>
                </div>
                <div className="teacher-overview-value">{formatCurrency(earningsData.thisMonth)}</div>
                <div className="teacher-overview-change">
                  <i className="material-icons">trending_up</i>
                  {t('earnings.overview.fromLastMonth')}
                </div>
              </div>

              <div className="teacher-overview-card warning">
                <div className="teacher-overview-header">
                  <span className="teacher-overview-title">{t('earnings.overview.pendingPayouts')}</span>
                  <div className="teacher-overview-icon">
                    <i className="material-icons">schedule</i>
                  </div>
                </div>
                <div className="teacher-overview-value">{formatCurrency(earningsData.pendingPayouts)}</div>
                <div className="teacher-overview-change">
                  <i className="material-icons">schedule</i>
                  {t('earnings.overview.nextPayout')}
                </div>
              </div>

              <div className="teacher-overview-card info">
                <div className="teacher-overview-header">
                  <span className="teacher-overview-title">{t('earnings.overview.withdrawnSoFar')}</span>
                  <div className="teacher-overview-icon">
                    <i className="material-icons">account_balance</i>
                  </div>
                </div>
                <div className="teacher-overview-value">{formatCurrency(earningsData.withdrawnSoFar)}</div>
                <div className="teacher-overview-change">
                  <i className="material-icons">check_circle</i>
                  {t('earnings.overview.successfullyWithdrawn')}
                </div>
              </div>

              <div className="teacher-overview-card">
                <div className="teacher-overview-header">
                  <span className="teacher-overview-title">{t('earnings.overview.conversionRate')}</span>
                  <div className="teacher-overview-icon">
                    <i className="material-icons">analytics</i>
                  </div>
                </div>
                <div className="teacher-overview-value">{earningsData.conversionRate}%</div>
                <div className="teacher-overview-change">
                  <i className="material-icons">trending_up</i>
                  {t('earnings.overview.viewsToPaidActions')}
                </div>
              </div>

              <div className="teacher-overview-card success">
                <div className="teacher-overview-header">
                  <span className="teacher-overview-title">{t('earnings.overview.topRevenueSource')}</span>
                  <div className="teacher-overview-icon">
                    <i className="material-icons">star</i>
                  </div>
                </div>
                <div className="teacher-overview-value">{earningsData.topRevenueSource}</div>
                <div className="teacher-overview-change">
                  <i className="material-icons">trending_up</i>
                  {t('earnings.overview.highestEarningActivity')}
                </div>
              </div>
            </div>
          </div>
        );

      case 'revenue':
        return (
          <div className="teacher-section">
            <div className="teacher-section-header">
              <div className="teacher-section-title">
                <i className="material-icons">pie_chart</i>
                {t('earnings.revenue.title')}
              </div>
            </div>

            <div className="revenue-breakdown-grid">
              {revenueByType.map((item, index) => (
                <div key={index} className="revenue-item">
                  <div className="revenue-item-header">
                    <div className="revenue-color" style={{ backgroundColor: item.color }}></div>
                    <span className="revenue-source">{item.source}</span>
                    <span className="revenue-percentage">{item.percentage}%</span>
                  </div>
                  <div className="revenue-amount">{formatCurrency(item.earnings)}</div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'payments':
        return (
          <div className="teacher-section">
            <div className="teacher-section-header">
              <div className="teacher-section-title">
                <i className="material-icons">account_balance_wallet</i>
                {t('earnings.payments.title')}
              </div>
              <button className="teacher-btn primary" onClick={() => setShowWithdrawModal(true)}>
                <i className="material-icons">account_balance</i>
                {t('earnings.payments.withdrawFunds')}
              </button>
            </div>

            <div className="payment-overview-grid">
              <div className="payment-card">
                <h4>{t('earnings.payments.currentBalance')}</h4>
                <div className="payment-amount">{formatCurrency(earningsData.pendingPayouts)}</div>
                <p className="payment-note">{t('earnings.payments.availableForWithdrawal')}</p>
              </div>

              <div className="payment-card">
                <h4>{t('earnings.payments.minimumPayout')}</h4>
                <div className="payment-amount">{formatCurrency(50)}</div>
                <p className="payment-note">{t('earnings.payments.thresholdToWithdraw')}</p>
              </div>

              <div className="payment-card">
                <h4>{t('earnings.payments.nextPayoutDate')}</h4>
                <div className="payment-amount">March 15, 2024</div>
                <p className="payment-note">{t('earnings.payments.automaticProcessing')}</p>
              </div>
            </div>

            <div className="payment-methods-section">
              <h4>{t('earnings.payments.paymentMethods')}</h4>
              <div className="payment-methods-grid">
                {paymentMethods.map((method) => (
                  <div key={method.id} className={`payment-method-card ${method.isDefault ? 'default' : ''}`}>
                    <div className="payment-method-info">
                      <div className="payment-method-icon">
                        <i className="material-icons">
                          {method.type === 'PayPal' ? 'account_balance_wallet' : 
                           method.type === 'Bank Transfer' ? 'account_balance' : 'account_circle'}
                        </i>
                      </div>
                      <div className="payment-method-details">
                        <h5>{method.type}</h5>
                        <p>{method.email || method.account}</p>
                      </div>
                    </div>
                    <div className="payment-method-actions">
                      {method.isDefault && <span className="default-badge">{t('earnings.payments.default')}</span>}
                      <button className="teacher-btn secondary" onClick={() => setShowPaymentModal(true)}>
                        <i className="material-icons">edit</i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'history':
        return (
          <div className="teacher-section">
            <div className="teacher-section-header">
              <div className="teacher-section-title">
                <i className="material-icons">history</i>
                {t('earnings.history.title')}
              </div>
              <div className="period-filter">
                <select 
                  value={selectedPeriod} 
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="teacher-select"
                >
                  <option value="last-7-days">{t('earnings.history.last7Days')}</option>
                  <option value="this-month">{t('earnings.history.thisMonth')}</option>
                  <option value="last-month">{t('earnings.history.lastMonth')}</option>
                  <option value="custom">{t('earnings.history.customRange')}</option>
                </select>
              </div>
            </div>

            <div className="teacher-table-container">
              <table className="teacher-data-table">
                <thead>
                  <tr>
                    <th>{t('earnings.history.date')}</th>
                    <th>{t('earnings.history.source')}</th>
                    <th>{t('earnings.history.amount')}</th>
                    <th>{t('earnings.history.status')}</th>
                    <th>{t('earnings.history.student')}</th>
                    <th>{t('earnings.history.notes')}</th>
                    <th>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {earningsHistory.map((earning) => (
                    <tr key={earning.id}>
                      <td>{new Date(earning.date).toLocaleDateString()}</td>
                      <td>
                        <div className="earning-source">
                          <i className="material-icons">{getSourceIcon(earning.type)}</i>
                          <span>{earning.source}</span>
                        </div>
                      </td>
                      <td className="earning-amount">{formatCurrency(earning.amount)}</td>
                      <td>{getStatusBadge(earning.status)}</td>
                      <td>{earning.student}</td>
                      <td className="earning-notes">{earning.notes}</td>
                      <td>
                        <div className="earning-actions">
                          <button className="teacher-btn secondary" title={t('common.view')}>
                            <i className="material-icons">visibility</i>
                          </button>
                          <button className="teacher-btn secondary" title={t('earnings.history.downloadInvoice', { defaultValue: 'Download Invoice' })}>
                            <i className="material-icons">receipt</i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'analytics':
        return (
          <div className="teacher-section">
            <div className="teacher-section-header">
              <div className="teacher-section-title">
                <i className="material-icons">analytics</i>
                {t('earnings.analytics.title')}
              </div>
              <div className="teacher-header-actions">
                <button className="teacher-btn secondary" onClick={() => exportEarnings('csv')}>
                  <i className="material-icons">download</i>
                  {t('earnings.analytics.exportAnalytics', { defaultValue: 'Export Analytics' })}
                </button>
              </div>
            </div>

            {/* Top Students Section */}
            <div className="analytics-section">
              <h3 className="analytics-section-title">
                <i className="material-icons">people</i>
                {t('earnings.analytics.topStudents')}
              </h3>
              <div className="analytics-cards-grid">
                {analyticsData.topStudents.map((student, index) => (
                  <div key={index} className="analytics-card student-card">
                    <div className="student-card-header">
                      <div className="student-avatar">
                        {student.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div className="student-info">
                        <h4>{student.name}</h4>
                        <p className="student-stats">
                          <span className="stat-item">
                            <i className="material-icons">attach_money</i>
                            {formatCurrency(student.totalSpent)}
                          </span>
                          <span className="stat-item">
                            <i className="material-icons">shopping_cart</i>
                            {student.purchases} {t('earnings.analytics.purchases')}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="student-courses">
                      <h5>{t('earnings.analytics.recentPurchases', { defaultValue: 'Recent Purchases:' })}</h5>
                      <div className="course-tags">
                        {student.courses.slice(0, 2).map((course, idx) => (
                          <span key={idx} className="course-tag">{course}</span>
                        ))}
                        {student.courses.length > 2 && (
                          <span className="course-tag more">+{student.courses.length - 2} more</span>
                        )}
                      </div>
                    </div>
                    <div className="student-last-purchase">
                      <small>{t('earnings.analytics.lastPurchase')}: {new Date(student.lastPurchase).toLocaleDateString()}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Courses Section */}
            <div className="analytics-section">
              <h3 className="analytics-section-title">
                <i className="material-icons">menu_book</i>
                {t('earnings.analytics.topCourses')}
              </h3>
              <div className="analytics-table-container">
                <table className="teacher-data-table">
                  <thead>
                    <tr>
                      <th>{t('earnings.analytics.courseName', { defaultValue: 'Course Name' })}</th>
                      <th>{t('earnings.analytics.revenue')}</th>
                      <th>{t('earnings.analytics.enrollments')}</th>
                      <th>{t('earnings.analytics.avgRating')}</th>
                      <th>{t('earnings.analytics.completionRate')}</th>
                      <th>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyticsData.topCourses.map((course, index) => (
                      <tr key={index}>
                        <td className="course-name">{course.name}</td>
                        <td className="course-revenue">{formatCurrency(course.revenue)}</td>
                        <td className="course-enrollments">{course.enrollments}</td>
                        <td className="course-rating">
                          <div className="rating-display">
                            <span className="stars">
                              {'★'.repeat(Math.floor(course.avgRating))}
                              {'☆'.repeat(5 - Math.floor(course.avgRating))}
                            </span>
                            <span className="rating-value">{course.avgRating}</span>
                          </div>
                        </td>
                        <td className="course-completion">
                          <div className="completion-bar">
                            <div 
                              className="completion-fill" 
                              style={{ width: `${course.completionRate}%` }}
                            ></div>
                            <span className="completion-text">{course.completionRate}%</span>
                          </div>
                        </td>
                        <td>
                          <div className="course-actions">
                            <button className="teacher-btn secondary" title="View Details">
                              <i className="material-icons">visibility</i>
                            </button>
                            <button className="teacher-btn secondary" title="Edit Course">
                              <i className="material-icons">edit</i>
                            </button>
                            <button className="teacher-btn secondary" title="Analytics">
                              <i className="material-icons">analytics</i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Earnings by Time Section */}
            <div className="analytics-section">
              <h3 className="analytics-section-title">
                <i className="material-icons">schedule</i>
                Earnings by Time Patterns
              </h3>
              
              <div className="time-analytics-grid">
                {/* Earnings by Day */}
                <div className="time-analytics-card">
                  <h4>Earnings by Day of Week</h4>
                  <div className="time-chart">
                    {analyticsData.earningsByTime.byDay.map((day, index) => (
                      <div key={index} className="time-bar-item">
                        <div className="time-bar-label">{day.day}</div>
                        <div className="time-bar-container">
                          <div 
                            className="time-bar-fill" 
                            style={{ 
                              width: `${(day.earnings / Math.max(...analyticsData.earningsByTime.byDay.map(d => d.earnings))) * 100}%`,
                              backgroundColor: day.earnings === Math.max(...analyticsData.earningsByTime.byDay.map(d => d.earnings)) ? '#27ae60' : '#3498db'
                            }}
                          ></div>
                          <span className="time-bar-value">{formatCurrency(day.earnings)}</span>
                        </div>
                        <div className="time-bar-sessions">{day.sessions} sessions</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Earnings by Hour */}
                <div className="time-analytics-card">
                  <h4>Earnings by Hour of Day</h4>
                  <div className="time-chart">
                    {analyticsData.earningsByTime.byHour.map((hour, index) => (
                      <div key={index} className="time-bar-item">
                        <div className="time-bar-label">{hour.hour}</div>
                        <div className="time-bar-container">
                          <div 
                            className="time-bar-fill" 
                            style={{ 
                              width: `${(hour.earnings / Math.max(...analyticsData.earningsByTime.byHour.map(h => h.earnings))) * 100}%`,
                              backgroundColor: hour.earnings === Math.max(...analyticsData.earningsByTime.byHour.map(h => h.earnings)) ? '#e74c3c' : '#f39c12'
                            }}
                          ></div>
                          <span className="time-bar-value">{formatCurrency(hour.earnings)}</span>
                        </div>
                        <div className="time-bar-sessions">{hour.sessions} sessions</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Insights */}
              <div className="analytics-insights">
                <h4>Key Insights</h4>
                <div className="insights-grid">
                  <div className="insight-card">
                    <i className="material-icons">trending_up</i>
                    <div className="insight-content">
                      <h5>Peak Earning Day</h5>
                      <p>Saturday generates the highest earnings with {formatCurrency(Math.max(...analyticsData.earningsByTime.byDay.map(d => d.earnings)))}</p>
                    </div>
                  </div>
                  <div className="insight-card">
                    <i className="material-icons">schedule</i>
                    <div className="insight-content">
                      <h5>Best Time Slot</h5>
                      <p>6 PM is your most profitable hour with {formatCurrency(Math.max(...analyticsData.earningsByTime.byHour.map(h => h.earnings)))}</p>
                    </div>
                  </div>
                  <div className="insight-card">
                    <i className="material-icons">star</i>
                    <div className="insight-content">
                      <h5>Top Performer</h5>
                      <p>Advanced English Grammar course generates {formatCurrency(Math.max(...analyticsData.topCourses.map(c => c.revenue)))} in revenue</p>
                    </div>
                  </div>
                  <div className="insight-card">
                    <i className="material-icons">person</i>
                    <div className="insight-content">
                      <h5>Most Valuable Student</h5>
                      <p>{analyticsData.topStudents[0].name} has spent {formatCurrency(analyticsData.topStudents[0].totalSpent)} across {analyticsData.topStudents[0].purchases} purchases</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="teacher-main-content">
      <div className="teacher-content-header">
        <h1 className="teacher-content-title">{t('earnings.title')}</h1>
        <p className="teacher-content-subtitle">{t('earnings.subtitle')}</p>
      </div>

      {/* Tabs Navigation */}
      <div className="teacher-tabs-container">
        <div className="teacher-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`teacher-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className="material-icons">{tab.icon}</i>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="teacher-tab-content">
        {renderTabContent()}
      </div>

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="teacher-modal-overlay" onClick={() => setShowWithdrawModal(false)}>
          <div className="teacher-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="teacher-modal-close" onClick={() => setShowWithdrawModal(false)}>×</button>
            
            <h2>Withdraw Funds</h2>
            
            <form className="teacher-form" onSubmit={handleWithdraw}>
              <div className="teacher-form-group">
                <label htmlFor="withdrawAmount">Amount to Withdraw</label>
                <input
                  type="number"
                  id="withdrawAmount"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                  min="50"
                  max={earningsData.pendingPayouts}
                  step="0.01"
                  required
                  placeholder="Enter amount"
                />
                <small>Available: {formatCurrency(earningsData.pendingPayouts)}</small>
              </div>

              <div className="teacher-form-group">
                <label htmlFor="paymentMethod">Payment Method</label>
                <select
                  id="paymentMethod"
                  value={selectedPaymentMethod}
                  onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                  required
                >
                  {paymentMethods.map(method => (
                    <option key={method.id} value={method.id}>
                      {method.type} - {method.email || method.account}
                    </option>
                  ))}
                </select>
              </div>

              <div className="teacher-form-actions">
                <button type="submit" className="teacher-btn primary">
                  <i className="material-icons">account_balance</i>
                  Withdraw Funds
                </button>
                <button 
                  type="button" 
                  className="teacher-btn secondary"
                  onClick={() => setShowWithdrawModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherSectionEarnings; 